import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  concatMap,
  EMPTY,
  filter,
  first,
  Observable,
  ReplaySubject,
  switchMap,
  take,
} from 'rxjs';

import { CartState } from '../../application/states/cart.state';
import { CheckoutState } from '../../application/states/checkout.state';
import { Product } from '../../models/product';
import { LocaleCurrencyPipe } from '../locale-currency.pipe';
import { ProductOptionsService } from '../product-options-dialog/product-options.service';
import { QuantityControlComponent } from '../quantity-control/quantity-control.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { ConfiguredRow, configuredRows } from './configured-rows';

/** The ticket a product limited to the ticket quantity hangs under, and how to add one more of it. */
export interface ConfiguredItemsTicket {
  name: string;
  add: () => Observable<unknown>;
}

/**
 * The cart rows of a product that has options, listed under its counter, each with its own −/+:
 * − takes one off that row, + adds the same variant again, or reopens a deal's builder with the same
 * picks for the buyer to confirm. See {@link configuredRows}.
 */
@Component({
  selector: 'plys-configured-items',
  templateUrl: './configured-items.component.html',
  styleUrls: ['./configured-items.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, TranslocoModule, LocaleCurrencyPipe, QuantityControlComponent],
})
export class ConfiguredItemsComponent {
  @Input() set product(product: Product) {
    this._product = product;
    this.product$.next(product);
  }
  get product(): Product {
    return this._product;
  }
  @Input() set parentProductId(id: string | null) {
    this._parentProductId = id ?? null;
    this.parentProductId$.next(id ?? null);
  }
  /** Scopes the rows, the limit and what + adds to that one ticket line (seats-first, PAUL-3787). */
  @Input() set ticketLineId(id: string | null) {
    this._ticketLineId = id ?? null;
    this.ticketLineId$.next(id ?? null);
  }
  /** The products offered next to this one, which the mutually-exclusive rule is checked against. */
  @Input() siblings: Product[] = [];
  /**
   * The ticket this product hangs under, for one limited to the ticket quantity: when every ticket has
   * one, + offers to add another ticket first instead of stopping.
   */
  @Input() ticket: ConfiguredItemsTicket | null = null;

  private _product: Product;
  private _parentProductId: string | null = null;
  private _ticketLineId: string | null = null;
  private readonly product$ = new ReplaySubject<Product>(1);
  private readonly parentProductId$ = new BehaviorSubject<string | null>(null);
  private readonly ticketLineId$ = new BehaviorSubject<string | null>(null);

  readonly rows$: Observable<ConfiguredRow[]> = configuredRows(
    this.cartState,
    this.product$,
    this.parentProductId$,
    this.ticketLineId$
  );
  readonly canAddMore$ = combineLatest([this.product$, this.ticketLineId$]).pipe(
    switchMap(([product, ticketLineId]) => this.cartState.canAddMore(product, ticketLineId))
  );
  readonly reserving$ = this.cartState.reserving$;

  constructor(
    private readonly cartState: CartState,
    private readonly checkoutState: CheckoutState,
    private readonly productOptions: ProductOptionsService,
    private readonly dialog: MatDialog,
    private readonly transloco: TranslocoService
  ) {}

  add(row: ConfiguredRow): void {
    this.canAddMore$
      .pipe(
        first(),
        concatMap((canAdd) =>
          canAdd || !this.ticket
            ? this.addAgain(row)
            : this.askForTicket().pipe(
                concatMap(() => this.ticket.add()),
                concatMap(() => this.addAgain(row))
              )
        ),
        take(1), // A closed dialog emits nothing: end quietly rather than as an error.
        catchError((err: Error) => {
          // A refused reservation has already reached the snackbar through the error interceptor.
          if (!(err instanceof HttpErrorResponse)) this.checkoutState.setError(err.message);
          return EMPTY;
        })
      )
      .subscribe();
  }

  remove(row: ConfiguredRow): void {
    row.remove().pipe(first()).subscribe();
  }

  private addAgain(row: ConfiguredRow): Observable<unknown> {
    return row.dealLines
      ? this.productOptions.addItem(
          this.product,
          this.siblings,
          this._parentProductId,
          row.dealLines,
          this._ticketLineId
        )
      : this.cartState.addItem(
          this.product,
          this.siblings,
          this._parentProductId,
          { productVariantId: row.variantId },
          this._ticketLineId
        );
  }

  /** Emits once when the buyer agrees to add another ticket; nothing when they close the dialog. */
  private askForTicket(): Observable<true> {
    const params = { extra: this.product.name, ticket: this.ticket.name };
    return this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: this.transloco.translate('ticket_needed.title'),
          message: this.transloco.translate('ticket_needed.message', params),
          confirmText: this.transloco.translate('ticket_needed.confirm'),
        },
      })
      .afterClosed()
      .pipe(filter((confirmed): confirmed is true => confirmed === true));
  }
}
